// LN Pro · ACTIONS adapters — Reddit (#1, already shipping) and Instagram (#2)
//
// PKT-0633. Each adapter translates its source into the shared ActionCard.
// Neither source's own wire contract is renamed: Reddit keeps `needs_reply` /
// `copied` because its live tests assert those literals, and translation is
// cheaper than a migration of a surface that works.

import type { RedditLead, RedditQueueState } from "./redditAnswerService";
import type { ActionCard, ActionQueueState, EdgeEvidence } from "./actionsContract";

// ── state mapping ────────────────────────────────────────────────────────────
// Bidirectional and total. Reddit's vocabulary is the fixed side.
const REDDIT_TO_GENERIC: Record<RedditQueueState, ActionQueueState> = {
  needs_reply: "needs_action",
  replied: "done",
  dismissed: "dismissed",
  all: "all",
};
const GENERIC_TO_REDDIT: Record<ActionQueueState, RedditQueueState> = {
  needs_action: "needs_reply",
  done: "replied",
  dismissed: "dismissed",
  all: "all",
};

export const toGenericState = (state: RedditQueueState): ActionQueueState => REDDIT_TO_GENERIC[state];
export const toRedditState = (state: ActionQueueState): RedditQueueState => GENERIC_TO_REDDIT[state];

// ── adapter #1 · Reddit ──────────────────────────────────────────────────────
/**
 * A Reddit lead as an ActionCard. Read-only projection: this does not call the
 * service, does not change what the desk renders today, and exists so Reddit
 * can be described in the same vocabulary as Instagram.
 *
 * Reddit carries no graph edges, so `evidence` is empty — NOT absent. An empty
 * array says "this source makes no graph claim"; an undefined would leave a
 * consumer guessing whether the claim was missing or unmade.
 */
export function redditLeadToCard(lead: RedditLead): ActionCard {
  const raw = (lead.raw_payload ?? {}) as Record<string, unknown>;
  const suggested = lead.digest_item?.suggested_reply_snapshot;
  return {
    id: lead.id,
    source: "reddit",
    source_url: lead.source_url,
    score: lead.score,
    verdict: lead.verdict,
    title: String(raw.title ?? lead.body_preview ?? "Reddit lead"),
    body_preview: lead.body_preview,
    why: lead.context_snippet,
    // `?? null`, not `?? ""` — the desk's own empty-string fallback is a
    // RENDERING choice for a text-shaped action. At the contract level, "there
    // is no suggested text" and "the suggested text is empty" are different
    // facts, and Instagram needs the first one to exist.
    suggested_text: suggested ?? null,
    disclosure_link: lead.digest_item?.credibility_link_snapshot ?? lead.credibility_link_url,
    evidence: [],
    posting_enabled: false,
  };
}

// ── adapter #2 · Instagram ───────────────────────────────────────────────────
/**
 * The shape PKT-0632 produces for one influencer→LocateNow edge.
 * Mirrors followEdge.mjs's resolveFollowEdge() return.
 */
export interface ResolvedFollowEdge {
  state: "verified" | "refuted" | "unknown";
  username: string;
  because: string;
}

export interface InstagramOpportunity {
  id: string;
  /** The candidate whose public post the operator would act on. */
  candidate: string;
  /** The post the operator opens and acts on themselves. */
  post_url: string;
  score: number;
  verdict: string;
  post_caption: string | null;
  /** Why this surfaced, in the operator's language. Grant's sentence lives here. */
  why: string | null;
  /** The bridge influencer(s), each carrying PKT-0632's verification state. */
  edges: ResolvedFollowEdge[];
  disclosure_link?: string | null;
}

/**
 * An Instagram opportunity as an ActionCard.
 *
 * 🔴 `suggested_text` IS NULL, AND THAT IS THE POINT. The action is "go like
 * one of their posts". There is nothing to draft, nothing to copy, and nothing
 * to personalize. Rendering an empty "Suggested reply" box with a "Copy reply"
 * button beneath it would be a card that lies about what it wants.
 *
 * This is the one place the packet's "no new card fields" prediction needed
 * qualifying: no field is ADDED, but an existing field has to be genuinely
 * nullable, and the primary verb becomes per-adapter rather than global.
 */
export function instagramOpportunityToCard(op: InstagramOpportunity): ActionCard {
  const evidence: EdgeEvidence[] = op.edges.map((edge) => ({
    kind: "influencer_follows_locatenow",
    subject: edge.username,
    state: edge.state,
    because: edge.because,
  }));
  return {
    id: op.id,
    source: "instagram",
    source_url: op.post_url,
    score: op.score,
    verdict: op.verdict,
    title: `@${op.candidate}`,
    body_preview: op.post_caption,
    why: op.why,
    suggested_text: null,
    disclosure_link: op.disclosure_link ?? null,
    evidence,
    posting_enabled: false,
  };
}

/**
 * The verbs an adapter offers the operator.
 *
 * There is no executor here and there is not meant to be one: every verb
 * records what a HUMAN did. `open` hands them a deep link; nothing in this
 * module can like, follow, comment or DM, and proof_required (3) asserts that
 * absence rather than trusting the comment.
 */
export function verbsFor(source: ActionCard["source"]): { primary: string; label: string }[] {
  return source === "reddit"
    ? [
        { primary: "copied", label: "Copy reply" },
        { primary: "marked_done", label: "Mark replied" },
        { primary: "dismissed", label: "Dismiss" },
      ]
    : [
        // No "copy" — there is no text. The operator opens the post and acts.
        { primary: "marked_done", label: "Mark done" },
        { primary: "dismissed", label: "Dismiss" },
      ];
}
